import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, ErrorState, Loading, Logo } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useSession } from '../session/SessionProvider';

interface JoinInfo {
  id: string;
  name: string;
  slug: string;
  city: string | null;
  state: string | null;
  logoUrl: string | null;
}

/** A dónde lleva el QR de invitación: crear cuenta (o entrar) y enviar la solicitud a esa iglesia. */
export function joinPath(churchId: string, auto = false): string {
  return `/unirse/${churchId}${auto ? '?auto=1' : ''}`;
}

export function JoinByInvitePage() {
  const { churchId = '' } = useParams();
  const [params] = useSearchParams();
  const { state } = useSession();
  const info = useAsync((signal) => api.get<JoinInfo>(`/multichurch/public/join/${encodeURIComponent(churchId)}`, signal), [churchId]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const autoSent = useRef(false);
  const signedIn = state.status === 'authenticated';
  const mine = useAsync(
    (signal) =>
      signedIn
        ? api.get<{ items: { church_id: string; status: string }[] }>('/multichurch/me/memberships', signal).then((r) => ({ signedIn: true, items: r.items.filter((m) => m.church_id === churchId) }))
        : Promise.resolve({ signedIn: false, items: [] as { church_id: string; status: string }[] }),
    [signedIn, churchId],
  );
  // Solo cuenta lo que se cargó con la sesión actual (evita enviar antes de saber si ya hay solicitud).
  const mineReady = mine.status === 'ready' && mine.data.signedIn === signedIn;
  const alreadyPending = mineReady && mine.data.items.some((m) => m.status === 'pending');
  const alreadyMember = mineReady && mine.data.items.some((m) => m.status === 'active');

  async function request() {
    setBusy(true);
    setResult(null);
    try {
      await api.post(`/multichurch/churches/${churchId}/membership-requests`);
      setResult({ tone: 'success', text: 'Solicitud enviada. Queda pendiente de aprobación de la iglesia; te avisaremos cuando te acepten.' });
      mine.reload();
    } catch (e) {
      setResult({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  // Al volver de crear la cuenta o de iniciar sesión, la solicitud se envía sola.
  useEffect(() => {
    if (signedIn && info.status === 'ready' && mineReady && !alreadyPending && !alreadyMember && params.get('auto') === '1' && !autoSent.current) {
      autoSent.current = true;
      void request();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, info.status, mineReady, alreadyPending, alreadyMember, params]);

  return (
    <div className="landing">
      <header className="landing__bar">
        <Link to="/" aria-label="KEORA">
          <Logo />
        </Link>
      </header>
      <main className="public public-event join-invite">
        {info.status === 'loading' || state.status === 'loading' ? <Loading /> : null}
        {info.status === 'error' ? <ErrorState error={info.error} /> : null}
        {info.status === 'ready' && state.status !== 'loading' ? (
          <>
            {info.data.logoUrl ? <img className="join-invite__logo" src={info.data.logoUrl} alt="" /> : null}
            <p className="eyebrow">Te invitan a unirte a</p>
            <h1>{info.data.name}</h1>
            {info.data.city || info.data.state ? <p className="muted">{[info.data.city, info.data.state].filter(Boolean).join(', ')}</p> : null}
            {result ? <Alert tone={result.tone}>{result.text}</Alert> : null}
            {signedIn && !result && alreadyMember ? (
              <>
                <Alert tone="success">Ya eres miembro de esta iglesia.</Alert>
                <Link to="/app" className="btn btn--primary btn--block">Ir a mi inicio</Link>
              </>
            ) : signedIn && !result && alreadyPending ? (
              <>
                <Alert tone="info">Tu solicitud ya fue enviada y está pendiente de aprobación.</Alert>
                <Link to="/app" className="btn btn--primary btn--block">Ir a mi inicio</Link>
              </>
            ) : signedIn ? (
              result?.tone === 'success' ? (
                <Link to="/app" className="btn btn--primary btn--block">
                  Ir a mi inicio
                </Link>
              ) : (
                <Button className="btn--block" busy={busy} onClick={() => void request()}>
                  Solicitar unirme
                </Button>
              )
            ) : (
              <>
                <p>Crea tu cuenta de KEORA y tu solicitud llegará directo a la iglesia.</p>
                <Link to={`/registro?unirse=${churchId}`} className="btn btn--primary btn--block">
                  Crear mi cuenta
                </Link>
                <p className="center">
                  ¿Ya tienes cuenta? <Link to={`/entrar?volver=${encodeURIComponent(joinPath(churchId, true))}`}>Iniciar sesión</Link>
                </p>
              </>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
}
